import { createEffect, on, Show } from 'solid-js'
import { settingStore, UserState, userStore } from '/web/store'
import { useGoogleReady } from '/web/shared/hooks'
import { useNavigate } from '@solidjs/router'

interface Props {
  show?: boolean
  user: UserState
  isRegister?: boolean
}

const LoginFormSocialButtons = (props: Props) => {
  const google = useGoogleReady()
  const state = settingStore()
  const navigate = useNavigate()

  let googleApi: any = null

  createEffect(
    on(
      () => google(),
      () => {
        const win: any = window
        const api = win.google?.accounts?.id

        if (!api) return

        if (state.config.serverConfig?.googleClientId) {
          api.initialize({
            client_id: state.config.serverConfig?.googleClientId,
            callback: (result: any) => {
              userStore.handleGoogleCallback('login', result, () => navigate('/dashboard'))
            },
          })

          googleApi = api
        }
      }
    )
  )

  const handleGoogleClick = () => {
    if (!googleApi) return
    googleApi.prompt((notification: any) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // One Tap not available, fall back to button flow
        // Create a temporary hidden container for Google's rendered button and click it
        const tempDiv = document.createElement('div')
        tempDiv.style.position = 'fixed'
        tempDiv.style.top = '-9999px'
        document.body.appendChild(tempDiv)
        googleApi.renderButton(tempDiv, {
          theme: 'filled_black',
          size: 'large',
          type: 'standard',
          text: props.isRegister ? 'signup_with' : 'signin_with',
        })
        // Wait for iframe to render then click it
        setTimeout(() => {
          const iframe = tempDiv.querySelector('iframe')
          const btn =
            tempDiv.querySelector('[role="button"]') ||
            tempDiv.querySelector('div[tabindex="0"]')
          if (btn) {
            ;(btn as HTMLElement).click()
          } else if (iframe) {
            // Can't click inside cross-origin iframe, remove and try popup
            try {
              ;(window as any).google.accounts.id.prompt()
            } catch (e) {
              // ignore
            }
          }
          setTimeout(() => document.body.removeChild(tempDiv), 5000)
        }, 300)
      }
    })
  }

  return (
    <Show when={props.show}>
      <button
        class="w-full h-[46px] flex items-center justify-center gap-[10px] rounded-[12px] border border-[rgba(255,255,255,0.12)] text-white font-rajdhani font-bold text-[14px] tracking-[0.4px] cursor-pointer transition-all duration-200 hover:bg-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.22)]"
        style="background: rgba(255,255,255,0.04)"
        onClick={handleGoogleClick}
        type="button"
      >
        <svg class="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        {props.isRegister ? 'Continue With Google' : 'Sign In With Google'}
      </button>

      <div class="flex items-center gap-3 my-[18px]">
        <span class="flex-1 h-px bg-[var(--cf-hairline)]" />
        <span class="font-rajdhani text-[11px] font-bold tracking-[1.6px] uppercase text-[rgba(255,255,255,0.35)]">
          or
        </span>
        <span class="flex-1 h-px bg-[var(--cf-hairline)]" />
      </div>
    </Show>
  )
}

export default LoginFormSocialButtons
